import * as React from 'react'
import { theme } from '../../theme'

export interface ModalDimensions {
	width: string
	height: string
	sidebarWidth: string
	controlsWidth: string
}

export interface ModalDimensionsContextValue {
	modalDimensions: ModalDimensions
	getScaledDimensions: (targetWidth: number, targetHeight: number) => { width: number; height: number }
}

const ModalDimensionsContext = React.createContext<ModalDimensionsContextValue | undefined>(undefined)

export interface Props {
	children: React.ReactNode
}

export const ModalDimensionsProvider = ({ children }: Props) => {
	// Calculate responsive dimensions based on screen size
	const getModalDimensions = () => {
		const screenWidth = window.innerWidth
		const screenHeight = window.innerHeight

		const modalWidth = Math.min(screenWidth * 0.8, 1400)
		const modalHeight = Math.min(screenHeight * 0.85, 900)

		// Calculate sidebar width as a percentage of modal width
		const calculatedSidebarWidth = modalWidth * 0.3 // 30% of modal width

		return {
			width: `${modalWidth}px`,
			height: `${modalHeight}px`,
			sidebarWidth: `${calculatedSidebarWidth}px`,
			controlsWidth: '330px',
		}
	}

	// Add state for dimensions to enable updates on resize
	const [modalDimensions, setModalDimensions] = React.useState(getModalDimensions())

	// Add a window resize handler to update dimensions
	React.useEffect(() => {
		const handleResize = () => {
			const newDimensions = getModalDimensions()
			// Only update if dimensions actually changed
			if (
				newDimensions.width !== modalDimensions.width ||
				newDimensions.height !== modalDimensions.height ||
				newDimensions.sidebarWidth !== modalDimensions.sidebarWidth
			) {
				setModalDimensions(newDimensions)
			}
		}

		window.addEventListener('resize', handleResize)
		return () => window.removeEventListener('resize', handleResize)
	}, [modalDimensions])

	// Calculate scaled dimensions that fit within the container while maintaining aspect ratio
	const getScaledDimensions = React.useCallback(
		(targetWidth: number, targetHeight: number) => {
			// Get available space (accounting for padding and controls)
			const paddingSpace = parseInt(theme.spacing.md, 10) * 3 // Reduced padding
			const sidebarWidth = parseInt(modalDimensions.sidebarWidth, 10)
			const controlsWidth = parseInt(modalDimensions.controlsWidth, 10)

			// Ensure we have valid numbers and set minimum dimensions
			const minDimension = 100 // Minimum size to prevent container from disappearing
			const availableWidth = Math.max(
				minDimension,
				window.innerWidth * 0.8 - sidebarWidth - controlsWidth - paddingSpace,
			)
			const availableHeight = Math.max(minDimension, window.innerHeight * 0.85 - 80 - paddingSpace) // Reduced vertical padding

			// Calculate scale factors for both dimensions
			const widthScale = availableWidth / targetWidth
			const heightScale = availableHeight / targetHeight

			// Use the smaller scale to ensure both dimensions fit, but don't go below minimum scale
			const minScale = 0.1 // Prevent extreme scaling
			const scale = Math.max(minScale, Math.min(widthScale, heightScale, 1)) // Don't scale up, only down

			return {
				width: Math.round(targetWidth * scale),
				height: Math.round(targetHeight * scale),
			}
		},
		[modalDimensions.sidebarWidth, modalDimensions.controlsWidth],
	)

	const value = React.useMemo(
		() => ({
			modalDimensions,
			getScaledDimensions,
		}),
		[modalDimensions, getScaledDimensions],
	)

	return <ModalDimensionsContext.Provider value={value}>{children}</ModalDimensionsContext.Provider>
}

export const useModalDimensions = (): ModalDimensionsContextValue => {
	const context = React.useContext(ModalDimensionsContext)
	if (context === undefined) {
		throw new Error('useModalDimensions must be used within a ModalDimensionsProvider')
	}
	return context
}
